"use client";

import type { ScaleTime } from "d3-scale";
import { motion } from "motion/react";
import { useId, useMemo } from "react";
import { chartCssVars, useChartStable, useYScale } from "./chart-context";
import { isReferenceAreaVisiblePhase } from "./y-domain-utils";

export type ReferenceLineOrientation = "horizontal" | "vertical";
export type ReferenceLineStrokeStyle = "solid" | "dashed";

export interface ReferenceLineProps {
  orientation: ReferenceLineOrientation;
  value: number | Date;
  yAxisId?: string | number;
  stroke?: string;
  strokeWidth?: number;
  strokeStyle?: ReferenceLineStrokeStyle;
  strokeDasharray?: string;
  label?: string;
  labelColor?: string;
  labelBackgroundColor?: string;
  labelPosition?: "start" | "end" | "middle";
  fadeEdges?: boolean;
  fadeEdgesLength?: number;
  className?: string;
}

const ENTER_FADE_MS = 380;

function clampFadeLength(length: number): number {
  return Math.min(45, Math.max(0, length));
}

export function ReferenceLine({
  orientation,
  value,
  yAxisId,
  stroke = chartCssVars.foregroundMuted,
  strokeWidth = 1.5,
  strokeStyle = "dashed",
  strokeDasharray = "4,4",
  label,
  labelColor = "var(--chart-5)",
  labelBackgroundColor = "var(--chart-background)",
  labelPosition = "end",
  fadeEdges = true,
  fadeEdgesLength = 10,
  className,
}: ReferenceLineProps) {
  const {
    innerWidth,
    innerHeight,
    xScale: ctxXScale,
    chartPhase,
    enterTransition,
  } = useChartStable();
  const xScale = ctxXScale as ScaleTime<number, number>;
  const yScale = useYScale(yAxisId);
  const uniqueId = useId().replace(/:/g, "");
  const maskId = `chart-reference-line-fade-${uniqueId}`;
  const gradientId = `${maskId}-gradient`;

  const geometry = useMemo(() => {
    if (orientation === "horizontal") {
      const y = yScale(value as number) ?? 0;
      return { x1: 0, y1: y, x2: innerWidth, y2: y };
    }
    const x = xScale(value as Date) ?? 0;
    return { x1: x, y1: 0, x2: x, y2: innerHeight };
  }, [orientation, value, xScale, yScale, innerWidth, innerHeight]);

  const fadeEdge = clampFadeLength(fadeEdgesLength);
  const edgeMask = fadeEdges ? `url(#${maskId})` : undefined;
  const lineDash = strokeStyle === "dashed" ? strokeDasharray : undefined;
  const visible = isReferenceAreaVisiblePhase(chartPhase);
  const fadeTransition =
    enterTransition && typeof enterTransition === "object"
      ? enterTransition
      : { duration: ENTER_FADE_MS / 1000, ease: "easeOut" as const };

  const labelX = (() => {
    if (orientation === "horizontal") {
      if (labelPosition === "start") return 6;
      if (labelPosition === "middle") return innerWidth / 2;
      return innerWidth - 6;
    }
    return geometry.x1;
  })();
  const labelY = (() => {
    if (orientation === "horizontal") return geometry.y1;
    if (labelPosition === "start") return 12;
    if (labelPosition === "middle") return innerHeight / 2;
    return innerHeight - 6;
  })();
  const labelAnchor = (() => {
    if (orientation === "horizontal") {
      if (labelPosition === "start") return "start";
      if (labelPosition === "middle") return "middle";
      return "end";
    }
    return "middle";
  })();

  const gradientDirection =
    orientation === "horizontal" ? { x1: "0%", x2: "100%", y1: "0%", y2: "0%" } : { x1: "0%", x2: "0%", y1: "0%", y2: "100%" };

  return (
    <motion.g
      animate={{ opacity: visible ? 1 : 0 }}
      aria-hidden="true"
      className={className ?? "chart-reference-line"}
      initial={{ opacity: 0 }}
      transition={fadeTransition}
    >
      {edgeMask ? (
        <defs>
          <linearGradient id={gradientId} {...gradientDirection}>
            <stop offset="0%" style={{ stopColor: "white", stopOpacity: 0 }} />
            <stop offset={`${fadeEdge}%`} style={{ stopColor: "white", stopOpacity: 1 }} />
            <stop offset={`${100 - fadeEdge}%`} style={{ stopColor: "white", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "white", stopOpacity: 0 }} />
          </linearGradient>
          <mask id={maskId}>
            <rect fill={`url(#${gradientId})`} height={innerHeight} width={innerWidth} x={0} y={0} />
          </mask>
        </defs>
      ) : null}
      <line
        mask={edgeMask}
        stroke={stroke}
        strokeDasharray={lineDash}
        strokeWidth={strokeWidth}
        x1={geometry.x1}
        x2={geometry.x2}
        y1={geometry.y1}
        y2={geometry.y2}
      />
      {label ? (
        <g>
          <rect
            fill={labelBackgroundColor}
            height={16}
            rx={3}
            width={label.length * 6.5 + 12}
            x={labelAnchor === "end" ? labelX - (label.length * 6.5 + 12) : labelAnchor === "middle" ? labelX - (label.length * 6.5 + 12) / 2 : labelX}
            y={labelY - 8}
          />
          <text
            fill={labelColor}
            fontSize={10}
            fontWeight={500}
            textAnchor={labelAnchor}
            x={labelX + (labelAnchor === "start" ? 6 : labelAnchor === "end" ? -6 : 0)}
            y={labelY + 3}
          >
            {label}
          </text>
        </g>
      ) : null}
    </motion.g>
  );
}

ReferenceLine.displayName = "ReferenceLine";

export default ReferenceLine;
